import type { Ref } from "vue";
import { computed, readonly, ref } from "vue";
import {
  confirmResetPassword as authConfirmResetPassword,
  confirmSignIn as authConfirmSignIn,
  confirmSignUp as authConfirmSignUp,
  fetchAuthSession as authFetchAuthSession,
  resendSignUpCode as authResendSignUpCode,
  resetPassword as authResetPassword,
  signIn as authSignIn,
  signOut as authSignOut,
  signUp as authSignUp,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { clearAllStores } from "@/stores";
import { routeNames } from "@/router/constants";
import { apolloClient } from "@/api/client";
import { getRouter } from "@/router";
import { config } from "@/config";
import { randomPassword } from "@/utils/passwords";
import { useIntl, useToast } from "@moroku/money-webapp-components";
import type { SignUpInput } from "@aws-amplify/auth";
import * as Sentry from "@sentry/vue";

export enum UserGroup {
  Broker = "broker",
}

const { t } = useIntl();
const toast = useToast();

export interface AuthUser {
  userId: string;
  email: string;
  groups: Set<UserGroup>;
}

export enum AuthState {
  SIGN_IN = "SIGN_IN",
  SIGN_UP = "SIGN_UP",
  CONFIRM_SIGN_IN = "CONFIRM_SIGN_IN",
  CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED = "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED",
  CONFIRM_SIGN_UP = "CONFIRM_SIGN_UP",
  FORGOT_PASSWORD = "FORGOT_PASSWORD",
  RESET_PASSWORD = "RESET_PASSWORD",
}

const isInit: Ref<boolean> = ref(false);
const authUser: Ref<AuthUser | null> = ref(null);
const loadingAuthUser = ref(false);
const errorMessage: Ref<string | null> = ref(null);
const authState: Ref<AuthState> = ref(AuthState.SIGN_IN);

const isInternalUser = computed(() => {
  const currentDomain = authUser.value?.email?.split("@")?.pop();
  return currentDomain === config.internalEmailDomain;
});

const isBroker = computed(() => {
  return authUser.value?.groups?.has(UserGroup.Broker);
});

const isSignInForm = computed(() => authState.value === AuthState.SIGN_IN || authState.value === AuthState.CONFIRM_SIGN_IN);

async function init() {
  if (isInit.value) {
    console.warn("useAuthUser already initialized");
    return;
  }
  isInit.value = true;

  await retrieveCurrentUser();
}

async function retrieveCurrentUser(): Promise<AuthUser | null> {
  errorMessage.value = null;
  try {
    const shouldRefetchUser = !authUser.value && !loadingAuthUser.value;
    loadingAuthUser.value = true;

    let session = await authFetchAuthSession({ forceRefresh: shouldRefetchUser });
    let idTokenPayload = session?.tokens?.idToken?.payload;
    const expiration = idTokenPayload?.exp && new Date(idTokenPayload.exp * 1000);

    if (!expiration || expiration < new Date()) {
      session = await authFetchAuthSession({ forceRefresh: true });
      idTokenPayload = session?.tokens?.idToken?.payload;
    }
    if (!session?.tokens?.idToken?.payload) {
      console.log("No session found");
      return setUserFromAttributes();
    }

    return setUserFromAttributes(idTokenPayload);
  } catch (err: any) {
    console.log("Error in retrieving auth user", err);
    errorMessage.value = err.toString();
    return setUserFromAttributes();
  } finally {
    loadingAuthUser.value = false;
  }
}

function setUserFromAttributes(attributes?: Record<string, any> | null): AuthUser | null {
  if (!attributes) {
    if (authUser.value) {
      signOut().then();
    }
    authUser.value = null;
    return null;
  }
  authUser.value = {
    userId: attributes.sub!,
    email: attributes.email!,
    groups: parseCognitoGroups(attributes?.["cognito:groups"] ?? []),
  };
  Sentry.setUser({ id: authUser.value.userId });

  return authUser.value;
}

// Only set groups that the webapp knows about
function parseCognitoGroups(groups: string[]) {
  return new Set(groups.filter(g => Object.values(UserGroup).includes(g as UserGroup)).map(g => g as UserGroup));
}

const signOut = async () => {
  loadingAuthUser.value = true;

  try {
    await authSignOut();
    await Promise.allSettled([apolloClient.clearStore(), clearAllStores()]);
    authState.value = AuthState.SIGN_IN;
    Sentry.setUser(null);
  } catch (e) {
    Sentry.captureException(e);
  }

  try {
    const router = getRouter();
    const { href } = router.resolve({ name: routeNames.HOME });
    window.history.replaceState({}, "", href);
    router.go(0);
  } catch (e) {
    Sentry.captureException(e);
    location.reload();
  }
};

Hub.listen("auth", async data => {
  switch (data.payload.event) {
    case "signedOut":
      retrieveCurrentUser();
  }
});

const signUp = async (email: string, firstName: string, lastName: string, password?: string | null) => {
  const params: SignUpInput = {
    username: email,
    password: config.passwordless || !password ? randomPassword() : password,
    options: {
      userAttributes: {
        given_name: firstName,
        family_name: lastName,
      },
      autoSignIn: !config.passwordless,
    },
  };

  if (config.passwordless) {
    // For passwordless we need to attempt signUp, but don't mind if the user has signed up already
    try {
      await authSignUp(params);
    } catch (err) {
      // Ignore sign-up errors
      console.warn("Error during passwordless sign up", err);
    }

    // For passwordless sign in we have autoconfirmed their account so initiate the sign in flow
    try {
      await signIn(email);
    } catch (err: any) {
      errorMessage.value = err.message;
      return false;
    }
  } else {
    try {
      const { nextStep } = await authSignUp(params);
      if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        authState.value = AuthState.CONFIRM_SIGN_UP;
      } else if (nextStep.signUpStep === "DONE") {
        await retrieveCurrentUser();
        return true;
      } else {
        console.warn("Unexpected sign up step", nextStep.signUpStep);
      }
      errorMessage.value = null;
    } catch (err: any) {
      errorMessage.value = err.message;
      return false;
    }
  }
  return true;
};

const signIn = async (username: string, password?: string) => {
  try {
    console.log("Signing in");
    const { nextStep } = await authSignIn({
      username,
      password,
      options: {
        authFlowType: password ? "USER_SRP_AUTH" : "CUSTOM_WITHOUT_SRP",
      },
    });
    if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
      authState.value = AuthState.CONFIRM_SIGN_UP;
    } else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
      authState.value = AuthState.CONFIRM_SIGN_IN;
    } else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      authState.value = AuthState.CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED;
    } else if (nextStep.signInStep === "RESET_PASSWORD") {
      authState.value = AuthState.RESET_PASSWORD;
    } else if (nextStep.signInStep === "DONE") {
      await retrieveCurrentUser();
      return true;
    } else {
      console.warn("Unexpected sign in step", nextStep.signInStep);
    }
    errorMessage.value = null;
  } catch (err: any) {
    console.warn("Error signing in", err.message);
    if (err.message.includes("User is not confirmed.")) {
      await authResendSignUpCode({ username });
      authState.value = AuthState.CONFIRM_SIGN_UP;
    } else if (err.message.includes("Incorrect username or password.") && config.passwordless) {
      errorMessage.value = t("onboarding_authentication_errors_passwordlessUnregistered");
    } else {
      errorMessage.value = err.message;
    }
    return false;
  }
};

const confirmSignIn = async (verificationCode: string) => {
  try {
    console.log("Confirming sign in verification");
    const { nextStep } = await authConfirmSignIn({ challengeResponse: verificationCode });
    errorMessage.value = null;
    if (nextStep.signInStep === "DONE") {
      await retrieveCurrentUser();
    } else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
      // If we had multiple challenges we would need to verify _cognitoUser.challengeParams hasn't changed
      errorMessage.value = t("onboarding_authentication_errors_invalidCode");
      return false;
    }
    return true;
  } catch (err: any) {
    if (!err.message || err.message.includes("Invalid session for the user.")) {
      errorMessage.value = t("onboarding_authentication_errors_sessionExpired");
      authState.value = AuthState.SIGN_IN;
    } else {
      errorMessage.value = err.message;
    }
    return false;
  }
};

const confirmSignUp = async (email: string, verificationCode: string, password?: string) => {
  try {
    console.log("Confirming sign up verification");
    await authConfirmSignUp({ username: email, confirmationCode: verificationCode });
    if (config.passwordless) {
      await signIn(email);
    } else if (password) {
      await signIn(email, password);
    } else {
      authState.value = AuthState.SIGN_IN;
    }
    errorMessage.value = null;
    return true;
  } catch (err: any) {
    if (err.message.includes("Invalid session for the user.")) {
      errorMessage.value = t("onboarding_authentication_errors_sessionExpired");
      authState.value = AuthState.SIGN_UP;
    } else if (err.message.includes("User cannot be confirmed. Current status is CONFIRMED")) {
      // Could automatically retry the sign in and go directly to the next step here
      errorMessage.value = t("onboarding_authentication_errors_alreadyRegistered");
      authState.value = AuthState.SIGN_IN;
    } else {
      errorMessage.value = err.message;
    }
    return false;
  }
};

const resendSignInCode = async (username: string, password?: string) => {
  try {
    // We need to submit an answer to trigger a resend of the password so submit an invalid code (0) with
    // some additional data indicating we want a new challenge issued
    const sendingToastId = toast.info(t("otp_sendingCodeAgain"));
    await signIn(username, password);
    errorMessage.value = null;
    toast.dismiss(sendingToastId);
    toast.success(t("otp_codeSentAgain"));
    return true;
  } catch (err: any) {
    if (err.message.includes("Invalid session for the user.")) {
      errorMessage.value = t("onboarding_authentication_errors_sessionExpired");
      authState.value = AuthState.SIGN_IN;
    } else {
      errorMessage.value = err.message;
    }
    toast.error("Error sending sign in code");
    Sentry.captureException(err);
    return false;
  }
};

const resendSignUpCode = async (email: string) => {
  try {
    const sendingToastId = toast.info(t("otp_sendingCodeAgain"));
    await authResendSignUpCode({ username: email });
    toast.dismiss(sendingToastId);
    errorMessage.value = null;
    toast.success(t("otp_codeSentAgain"));
    return true;
  } catch (err: any) {
    errorMessage.value = err.message;
    toast.error("Error sending sign up code");
    return false;
  }
};

const showSignIn = () => {
  authState.value = AuthState.SIGN_IN;
  errorMessage.value = null;
};
const showSignUp = () => {
  authState.value = AuthState.SIGN_UP;
  errorMessage.value = null;
};

const showForgotPassword = () => {
  authState.value = AuthState.FORGOT_PASSWORD;
  errorMessage.value = null;
};

const forgotPassword = async (email: string) => {
  try {
    await authResetPassword({ username: email });
    errorMessage.value = null;
    authState.value = AuthState.RESET_PASSWORD;
    return true;
  } catch (err: any) {
    errorMessage.value = err.message;
    return false;
  }
};

const confirmForgotPassword = async (email: string, confirmationCode: string, newPassword: string) => {
  try {
    errorMessage.value = null;
    await authConfirmResetPassword({ username: email, confirmationCode, newPassword });
    return await signIn(email, newPassword);
  } catch (err: any) {
    errorMessage.value = err.message;
    return false;
  }
};

export default function useAuthUser() {
  return {
    // The signed-in user's auth properties. Use customer queries from customer details.
    authState: readonly(authState),
    authUser,
    confirmForgotPassword,
    confirmSignIn,
    confirmSignUp,
    errorMessage: readonly(errorMessage),
    forgotPassword,
    init,
    isInternalUser,
    isBroker,
    isSignInForm,
    loadingAuthUser,
    resendSignInCode,
    resendSignUpCode,
    showForgotPassword,
    showSignIn,
    showSignUp,
    signIn,
    signOut,
    signUp,
    retrieveCurrentUser,
  };
}
